/**
 * useFeedback — state + actions for the feedback widget (replicated from
 * brospace, adapted to Stell).
 *
 * Differences from brospace:
 *   - Auth/user comes from `useSession()` (GET /api/auth/me), not an AuthProvider.
 *   - There is no global toast viewport, so submit success/failure is surfaced
 *     inline in the panel via `submitState` / `issueUrl` / `submitError`.
 *   - Screenshot via html2canvas-pro; AI draft via Claude (/api/feedback/*).
 */
import { useCallback, useRef, useState } from 'react';
import html2canvas from 'html2canvas-pro';
import { getConsoleErrors } from '../../lib/errorCapture';
import { getNetworkCalls } from '../../lib/networkCapture';

export type FeedbackCategory = 'bug' | 'feature' | 'question';
export type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackState {
  category: FeedbackCategory;
  title: string;
  description: string;
  screenshotDataUrl: string | null;
  submitState: SubmitState;
  issueUrl: string | null;
  submitError: string | null;
  // AI draft
  isGenerating: boolean;
  aiDraft: string | null;
  aiAvailable: boolean | null; // null = not yet checked
  aiError: string | null;
}

/** Svarkodene fra AI-porten på `generate-description` (FF-13, #181). */
const AI_BLOCKED = new Set(['ai_disabled', 'ai_budget', 'ai_cost_ceiling']);

const DEFAULT_STATE: FeedbackState = {
  category: 'bug',
  title: '',
  description: '',
  screenshotDataUrl: null,
  submitState: 'idle',
  issueUrl: null,
  submitError: null,
  isGenerating: false,
  aiDraft: null,
  aiAvailable: null,
  aiError: null,
};

function dataUrlToBlob(dataUrl: string): Blob {
  const [header = '', data = ''] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const binary = atob(data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new Blob([array], { type: mime });
}

export function useFeedback() {
  const [state, setState] = useState<FeedbackState>({ ...DEFAULT_STATE });
  const abortRef = useRef<AbortController | null>(null);
  const aiCheckedRef = useRef(false);

  const setCategory = useCallback((category: FeedbackCategory) => {
    setState((p) => ({ ...p, category }));
  }, []);
  const setTitle = useCallback((title: string) => setState((p) => ({ ...p, title })), []);
  const setDescription = useCallback(
    (description: string) => setState((p) => ({ ...p, description })),
    [],
  );
  const setScreenshotDataUrl = useCallback(
    (screenshotDataUrl: string | null) => setState((p) => ({ ...p, screenshotDataUrl })),
    [],
  );

  const reset = useCallback(() => {
    aiCheckedRef.current = false;
    setState({ ...DEFAULT_STATE });
  }, []);

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    try {
      const canvas = await html2canvas(document.body, {
        scale: window.devicePixelRatio > 1 ? 0.5 : 1,
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }, []);

  const submit = useCallback(async () => {
    setState((p) => ({ ...p, submitState: 'submitting', submitError: null }));
    try {
      const formData = new FormData();
      formData.append('title', state.title);
      formData.append('description', state.description);
      formData.append('category', state.category);
      formData.append('url', window.location.href);

      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        consoleErrors: getConsoleErrors(),
        networkCalls: getNetworkCalls(),
      };
      formData.append('metadata', JSON.stringify(metadata));

      if (state.screenshotDataUrl) {
        formData.append('screenshot', dataUrlToBlob(state.screenshotDataUrl), 'screenshot.png');
      }

      // Do NOT set Content-Type — the browser sets the multipart boundary.
      const res = await fetch('/api/feedback', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { issueUrl?: string };
      setState((p) => ({ ...p, submitState: 'success', issueUrl: data.issueUrl ?? null }));
    } catch {
      setState((p) => ({ ...p, submitState: 'error', submitError: 'submit_failed' }));
    }
  }, [state.title, state.description, state.category, state.screenshotDataUrl]);

  // ── AI draft ──
  const checkAiAvailability = useCallback(async () => {
    if (aiCheckedRef.current) return; // check once per mount
    aiCheckedRef.current = true;
    try {
      const res = await fetch('/api/feedback/ai-status', { credentials: 'same-origin' });
      const available = res.ok ? ((await res.json()) as { available: boolean }).available : false;
      setState((p) => ({ ...p, aiAvailable: available }));
    } catch {
      setState((p) => ({ ...p, aiAvailable: false }));
    }
  }, []);

  const generate = useCallback(async () => {
    if (state.title.trim().length < 3 || !state.screenshotDataUrl) return;

    const previousDescription = state.description;
    const controller = new AbortController();
    abortRef.current = controller;
    setState((p) => ({ ...p, isGenerating: true, aiError: null }));

    try {
      const res = await fetch('/api/feedback/generate-description', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        signal: controller.signal,
        body: JSON.stringify({
          title: state.title,
          category: state.category,
          screenshotBase64: state.screenshotDataUrl.split(',')[1] ?? null,
          consoleErrors: getConsoleErrors().map((e) => e.message),
          networkCalls: getNetworkCalls(),
        }),
      });
      if (!res.ok) {
        // Familiens AI er av eller budsjettet/dagstaket er brukt opp (FF-13, #181):
        // en egen melding, og knappen skjules, så ingen prøver igjen og igjen.
        const code = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
        if (code && AI_BLOCKED.has(code)) {
          setState((p) => ({ ...p, isGenerating: false, aiAvailable: false, aiError: 'ai_unavailable' }));
          return;
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data = (await res.json()) as { description: string };
      setState((p) => ({
        ...p,
        isGenerating: false,
        description: data.description,
        aiDraft: data.description,
        aiError: null,
      }));
    } catch (err) {
      // AbortError (user pressed Cancel) restores the prior text. Check by name
      // rather than `instanceof DOMException` so it works without that global.
      if (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError') {
        setState((p) => ({ ...p, isGenerating: false, description: previousDescription }));
        return;
      }
      setState((p) => ({
        ...p,
        isGenerating: false,
        aiError: 'generate_failed',
      }));
    }
  }, [state.title, state.category, state.screenshotDataUrl, state.description]);

  const cancelGenerate = useCallback(() => abortRef.current?.abort(), []);
  const clearAiError = useCallback(
    () => setState((p) => (p.aiError ? { ...p, aiError: null } : p)),
    [],
  );

  return {
    state,
    setCategory,
    setTitle,
    setDescription,
    setScreenshotDataUrl,
    captureScreenshot,
    submit,
    reset,
    generate,
    cancelGenerate,
    checkAiAvailability,
    clearAiError,
  };
}
